"use client";

import { AlertCircle } from "lucide-react";

interface FormErrorProps {
  /**
   * Error message to display
   */
  message: string | null;

  /**
   * Optional CSS class to apply to the container
   */
  className?: string;

  /**
   * Whether this is a field-level error
   * @default false
   */
  isFieldError?: boolean;

  /**
   * ID for accessibility
   */
  id?: string;

  /**
   * Whether to show an icon
   * @default true
   */
  showIcon?: boolean;
}

/**
 * Standardized error component for forms
 *
 * @example
 * ```tsx
 * // Form-level error
 * <FormError message={error} />
 *
 * // Field-level error
 * <FormError message={fieldError} isFieldError={true} />
 * ```
 */
const FIELD_ERROR_CLASSES = "text-sm mt-1";
const FORM_ERROR_CLASSES =
  "p-3 bg-danger-50 rounded-md border border-danger-200 flex items-center gap-2 mb-4";

function buildErrorClasses(isFieldError: boolean, className: string): string {
  const variantClasses = isFieldError
    ? FIELD_ERROR_CLASSES
    : FORM_ERROR_CLASSES;

  return `text-danger-600 ${variantClasses} ${className}`.trim();
}

function FormErrorIcon({
  isFieldError,
  showIcon,
}: {
  isFieldError: boolean;
  showIcon: boolean;
}) {
  if (!showIcon || isFieldError) return null;

  return <AlertCircle aria-hidden="true" className="h-5 w-5 flex-shrink-0" />;
}

export function FormError({
  className = "",
  id,
  isFieldError = false,
  message,
  showIcon = true,
}: FormErrorProps) {
  if (!message) return null;

  const classes = buildErrorClasses(isFieldError, className);

  return (
    <div aria-live="assertive" className={classes} id={id} role="alert">
      <FormErrorIcon isFieldError={isFieldError} showIcon={showIcon} />
      <span>{message}</span>
    </div>
  );
}
